import * as ex from "excalibur";
import { CONFIG } from "../config";
import { TERRAIN_SPRITES, STREET_SPRITES, drawLandscapeSprite, } from "./LandscapeAtlas";
import { MAP_CENTER, MAP_GRID_SIZE, edgeBitmask } from "@dein-go-city/shared";
const SCALE = 1;
const TW = CONFIG.TILE_WIDTH;
const TH = CONFIG.TILE_HEIGHT;
// Reference x-position for chunk col=0. Matches the left edge of the 101×101 tile grid
// so that existing tile-world positions map to consistent chunk indices.
const CHUNK_REF_X = -(MAP_GRID_SIZE * (TW / 2)); // −3232
// Each chunk covers CHUNK_WORLD_SIZE × CHUNK_WORLD_SIZE world units.
const CHUNK_WORLD_SIZE = 256;
// One extra chunk of lookahead in every direction before loading/unloading.
const LOAD_BUFFER = CHUNK_WORLD_SIZE;
// Target upper bound on cached chunk bitmaps (256×256×4 B ≈ 256 KiB each → ~32 MiB) for the
// normal case. Active (currently on-screen) chunks are never evicted regardless of this cap, so
// at extreme zoom-out the true ceiling is active-chunk-count, not this constant — see
// MAX_CHUNKS_PER_AXIS below for what actually bounds that.
const MAX_CACHED_CHUNKS = 128;
// Backstop on chunks visible per axis in a single frame. CONFIG.ABSOLUTE_MIN_ZOOM (GameScene's
// real control on how far out the camera can zoom) is what this guards against — without it,
// any future zoom-clamp bug would ask this loop to rasterize an unbounded number of canvases and
// stall the renderer. Derived (not picked) from the worst legitimate case — min zoom on the
// widest viewport worth supporting — so changing either constant forces a look at the other.
const MAX_SUPPORTED_VIEWPORT_PX = 3840;
const MAX_CHUNKS_PER_AXIS = Math.ceil(MAX_SUPPORTED_VIEWPORT_PX / CONFIG.ABSOLUTE_MIN_ZOOM / CHUNK_WORLD_SIZE) + 2;
// Tallest sprite drawn in the ground layer (currently the 131px-source tree, destH 56) — the
// chunk draw loop's vMin margin must overdraw by this much, not the flat-grass constant, or
// tall vegetation overhanging from the chunk below gets clipped. Also used (conservatively,
// same constant for every cell type) to bound which chunks a single tile's sprite can touch.
const MAX_DEST_H = Math.max(...Object.values(TERRAIN_SPRITES).map((s) => s.destH), ...Object.values(STREET_SPRITES).map((s) => s.destH));
// Full 16-case autotile lookup (see edgeBitmask in shared/terrain.ts: bit0=N, bit1=E, bit2=S,
// bit3=W). Only mask 0 (isolated) has no dedicated sprite — everything else does.
const STREET_TILE_FOR_MASK = new Map([
    [0b0001, { sprite: STREET_SPRITES.endN, flip: false }],
    [0b0010, { sprite: STREET_SPRITES.endE, flip: false }],
    [0b0100, { sprite: STREET_SPRITES.endS, flip: false }],
    [0b1000, { sprite: STREET_SPRITES.endW, flip: false }],
    [0b0011, { sprite: STREET_SPRITES.cornerNE, flip: false }],
    [0b0110, { sprite: STREET_SPRITES.cornerSE, flip: false }],
    [0b1100, { sprite: STREET_SPRITES.cornerSW, flip: false }],
    [0b1001, { sprite: STREET_SPRITES.cornerNW, flip: false }],
    [0b0101, { sprite: STREET_SPRITES.straightNS, flip: false }],
    [0b1010, { sprite: STREET_SPRITES.straightNS, flip: true }], // flip swaps N<->W,E<->S
    [0b1101, { sprite: STREET_SPRITES.tNSW, flip: false }], // N+S+W, no E
    [0b0111, { sprite: STREET_SPRITES.tNSE, flip: false }], // N+S+E, no W
    [0b1110, { sprite: STREET_SPRITES.tEWS, flip: false }], // E+W+S, no N
    [0b1011, { sprite: STREET_SPRITES.tEWN, flip: false }], // E+W+N, no S
    [0b1111, { sprite: STREET_SPRITES.cross, flip: false }],
]);
function chunkKeyForWorld(x, y) {
    const col = Math.floor((x - CHUNK_REF_X) / CHUNK_WORLD_SIZE);
    const row = Math.floor(y / CHUNK_WORLD_SIZE);
    return `${col},${row}`;
}
// World-space bounding box a single tile's ground sprite can occupy, used to find every
// chunk that must be re-rasterized when that tile's street/terrain rendering changes.
function chunkKeysForTile(x, y) {
    const u = x - y;
    const v = x + y;
    const left = (u * TW) / 2 - TW / 2;
    const top = (v * TH) / 2;
    const keys = new Set();
    // Sprites are bottom-aligned to a shared ground line (see drawLandscapeSprite), so a
    // taller-than-standard sprite extends *above* `top`, not just below it — margin both ways.
    for (const wx of [left, left + TW]) {
        for (const wy of [top - MAX_DEST_H, top + MAX_DEST_H]) {
            keys.add(chunkKeyForWorld(wx, wy));
        }
    }
    return keys;
}
export class TileChunkManager {
    terrain;
    active = new Map();
    // Rasterized chunks are kept after scrolling off-screen (LRU by insertion order)
    // so panning back re-adds the cached bitmap instead of redrawing the canvas.
    cache = new Map();
    streets = new Set();
    constructor(terrain) {
        this.terrain = terrain;
    }
    // Call from GameScene.onPreUpdate every frame.
    update(scene, camera, engine) {
        const zoom = camera.zoom;
        const halfW = engine.screen.drawWidth / zoom / 2;
        const halfH = engine.screen.drawHeight / zoom / 2;
        // Chunk indices are unbounded integers — no clamping to a fixed grid, so an excessive
        // range (e.g. from a zoom-clamp bug) is truncated around the camera below.
        let colMin = Math.floor((camera.pos.x - halfW - LOAD_BUFFER - CHUNK_REF_X) / CHUNK_WORLD_SIZE);
        let colMax = Math.floor((camera.pos.x + halfW + LOAD_BUFFER - CHUNK_REF_X) / CHUNK_WORLD_SIZE);
        let rowMin = Math.floor((camera.pos.y - halfH - LOAD_BUFFER) / CHUNK_WORLD_SIZE);
        let rowMax = Math.floor((camera.pos.y + halfH + LOAD_BUFFER) / CHUNK_WORLD_SIZE);
        if (colMax - colMin + 1 > MAX_CHUNKS_PER_AXIS) {
            const colCenter = Math.round((colMin + colMax) / 2);
            colMin = colCenter - Math.floor(MAX_CHUNKS_PER_AXIS / 2);
            colMax = colCenter + Math.floor(MAX_CHUNKS_PER_AXIS / 2);
        }
        if (rowMax - rowMin + 1 > MAX_CHUNKS_PER_AXIS) {
            const rowCenter = Math.round((rowMin + rowMax) / 2);
            rowMin = rowCenter - Math.floor(MAX_CHUNKS_PER_AXIS / 2);
            rowMax = rowCenter + Math.floor(MAX_CHUNKS_PER_AXIS / 2);
        }
        const needed = new Set();
        for (let row = rowMin; row <= rowMax; row++) {
            for (let col = colMin; col <= colMax; col++) {
                needed.add(`${col},${row}`);
            }
        }
        for (const key of needed) {
            if (this.active.has(key))
                continue;
            const actor = this.getOrBuildChunk(key);
            scene.add(actor);
            this.active.set(key, actor);
        }
        for (const [key, actor] of this.active) {
            // Remove (not kill) so the cached actor can be re-added later.
            if (!needed.has(key)) {
                scene.remove(actor);
                this.active.delete(key);
                // Just went idle — refresh its cache position so LRU order reflects true recency
                // instead of whenever it was originally built or last cache-hit.
                this.cache.delete(key);
                this.cache.set(key, actor);
            }
        }
    }
    // Replaces the live street-tile set and re-rasterizes only the chunks touched by tiles that
    // actually changed (plus their 4 neighbors, since autotiling means a neighbor's sprite can
    // change too). Street mutations are rare (one placement per event) and touch a handful of
    // chunks — no visible hitch. Cheap no-op on the very first call if there are no streets yet.
    setStreets(next) {
        const changed = [];
        for (const k of next)
            if (!this.streets.has(k))
                changed.push(k);
        for (const k of this.streets)
            if (!next.has(k))
                changed.push(k);
        this.streets = new Set(next);
        if (changed.length === 0)
            return;
        const dirtyChunks = new Set();
        for (const k of changed) {
            const [xStr, yStr] = k.split(",");
            const x = parseInt(xStr, 10);
            const y = parseInt(yStr, 10);
            for (const [dx, dy] of [
                [0, 0],
                [1, 0],
                [-1, 0],
                [0, 1],
                [0, -1],
            ]) {
                for (const chunkKey of chunkKeysForTile(x + dx, y + dy))
                    dirtyChunks.add(chunkKey);
            }
        }
        for (const chunkKey of dirtyChunks) {
            this.cache.delete(chunkKey);
            const activeActor = this.active.get(chunkKey);
            if (!activeActor)
                continue;
            activeActor.graphics.use(this.buildChunkGraphic(chunkKey));
            this.cache.set(chunkKey, activeActor); // keep cache in sync — this chunk is still fresh
        }
    }
    isNetworkTile(x, y) {
        return (this.streets.has(`${x},${y}`) || (x === MAP_CENTER && y === MAP_CENTER));
    }
    drawTile(ctx, c, r, px, py) {
        if (this.streets.has(`${c},${r}`)) {
            const mask = edgeBitmask(c, r, (x, y) => this.isNetworkTile(x, y));
            const tile = STREET_TILE_FOR_MASK.get(mask);
            if (tile)
                drawLandscapeSprite(ctx, tile.sprite, px, py, tile.flip);
            else
                drawLandscapeSprite(ctx, STREET_SPRITES.generic, px, py, false); // isolated (mask 0)
            return;
        }
        const info = TERRAIN_SPRITES[this.terrain.cellAt(c, r)];
        drawLandscapeSprite(ctx, info, px, py, false);
    }
    getOrBuildChunk(key) {
        let actor = this.cache.get(key);
        if (actor) {
            // Refresh LRU position
            this.cache.delete(key);
            this.cache.set(key, actor);
            return actor;
        }
        actor = this.buildChunk(key);
        this.cache.set(key, actor);
        // Evict oldest-first (Map iteration order = insertion order) until back at the cap or
        // no evictable (non-active) entry remains — a stable key snapshot up front means this
        // always finishes the pass instead of depending on live-iteration-during-delete subtleties.
        for (const oldKey of [...this.cache.keys()]) {
            if (this.cache.size <= MAX_CACHED_CHUNKS)
                break;
            if (this.active.has(oldKey))
                continue;
            this.cache.delete(oldKey);
        }
        return actor;
    }
    buildChunkGraphic(key) {
        const [colStr, rowStr] = key.split(",");
        const col = parseInt(colStr, 10);
        const row = parseInt(rowStr, 10);
        const originX = CHUNK_REF_X + col * CHUNK_WORLD_SIZE;
        const originY = row * CHUNK_WORLD_SIZE;
        const canvasSize = CHUNK_WORLD_SIZE * SCALE;
        const S = CHUNK_WORLD_SIZE;
        const drawTile = this.drawTile.bind(this);
        return new ex.Canvas({
            width: canvasSize,
            height: canvasSize,
            cache: true,
            filtering: ex.ImageFiltering.Pixel,
            draw(ctx) {
                ctx.save();
                ctx.beginPath();
                ctx.rect(0, 0, canvasSize, canvasSize);
                ctx.clip();
                // Iterate iso lattice points (u, v) where u = c−r, v = c+r, u+v always even.
                // Sprite top-left in world: (u·TW/2 − TW/2, v·TH/2).
                // Range: sprites whose bounding box intersects this chunk, using the tallest
                // possible ground sprite as overdraw margin (see MAX_DEST_H).
                const vMin = Math.floor((originY - MAX_DEST_H) / (TH / 2));
                const vMax = Math.ceil((originY + S) / (TH / 2));
                const uMin = Math.floor((originX - TW / 2) / (TW / 2));
                const uMax = Math.ceil((originX + S + TW / 2) / (TW / 2));
                for (let v = vMin; v <= vMax; v++) {
                    for (let u = uMin; u <= uMax; u++) {
                        if (((u + v) & 1) !== 0)
                            continue; // u+v must be even (c,r ∈ ℤ)
                        const c = (u + v) / 2;
                        const r = (v - u) / 2;
                        const px = ((u * TW) / 2 - TW / 2 - originX) * SCALE;
                        const py = ((v * TH) / 2 - originY) * SCALE;
                        drawTile(ctx, c, r, px, py);
                    }
                }
                ctx.restore();
            },
        });
    }
    buildChunk(key) {
        const [colStr, rowStr] = key.split(",");
        const col = parseInt(colStr, 10);
        const row = parseInt(rowStr, 10);
        const originX = CHUNK_REF_X + col * CHUNK_WORLD_SIZE;
        const originY = row * CHUNK_WORLD_SIZE;
        const actor = new ex.Actor({
            pos: ex.vec(originX, originY),
            anchor: ex.vec(0, 0),
        });
        actor.transform.z = -1;
        actor.graphics.use(this.buildChunkGraphic(key));
        actor.graphics.forceOnScreen = true;
        return actor;
    }
}
